export const MANAGE_FIELD_KEY = {
  vehicleVin: 'vehicle.vin',
  customerCompactView: 'customer.compactView',
  customerCompany: 'customer.company',
  customerBillingEmail: 'customer.billingEmail',
  customerCompanyPhone: 'customer.companyPhone',
  customerAltPhone: 'customer.altPhone',
  customerAltEmail: 'customer.altEmail',
  customerNote: 'customer.note',
  originBusinessName: 'origin.businessName',
  originNote: 'origin.note',
  destinationBusinessName: 'destination.businessName',
  destinationNote: 'destination.note',
  shipmentTags: 'shipment.tags',
  shipmentCustomerNote: 'shipment.customerNote',
  paymentDefaultMethods: 'payment.defaultMethods',
} as const

export type ManageFieldKey =
  (typeof MANAGE_FIELD_KEY)[keyof typeof MANAGE_FIELD_KEY]

export const MANAGE_FIELD_SCOPE = {
  both: 'both',
  order: 'order',
  quote: 'quote',
} as const

export type ManageFieldScope =
  (typeof MANAGE_FIELD_SCOPE)[keyof typeof MANAGE_FIELD_SCOPE]

export interface ManageFieldOption {
  key: ManageFieldKey
  labelKey: string
  scope?: ManageFieldScope
}

export interface ManageFieldSection {
  titleKey: string
  options: readonly ManageFieldOption[]
}

export const MANAGE_FIELD_SECTIONS: readonly ManageFieldSection[] = [
  {
    titleKey: 'manageFields.sections.vehicle',
    options: [
      {
        key: MANAGE_FIELD_KEY.vehicleVin,
        labelKey: 'manageFields.options.vin',
      },
    ],
  },
  {
    titleKey: 'manageFields.sections.customerDetails',
    options: [
      {
        key: MANAGE_FIELD_KEY.customerCompactView,
        labelKey: 'manageFields.options.compactView',
      },
      {
        key: MANAGE_FIELD_KEY.customerCompany,
        labelKey: 'manageFields.options.company',
      },
      {
        key: MANAGE_FIELD_KEY.customerBillingEmail,
        labelKey: 'manageFields.options.billingEmail',
      },
      {
        key: MANAGE_FIELD_KEY.customerCompanyPhone,
        labelKey: 'manageFields.options.companyPhone',
      },
      {
        key: MANAGE_FIELD_KEY.customerAltPhone,
        labelKey: 'manageFields.options.altPhone',
      },
      {
        key: MANAGE_FIELD_KEY.customerAltEmail,
        labelKey: 'manageFields.options.altEmail',
      },
      {
        key: MANAGE_FIELD_KEY.customerNote,
        labelKey: 'manageFields.options.additionalNote',
      },
    ],
  },
  {
    titleKey: 'manageFields.sections.origin',
    options: [
      {
        key: MANAGE_FIELD_KEY.originBusinessName,
        labelKey: 'manageFields.options.businessName',
        scope: MANAGE_FIELD_SCOPE.order,
      },
      {
        key: MANAGE_FIELD_KEY.originNote,
        labelKey: 'manageFields.options.additionalNote',
        scope: MANAGE_FIELD_SCOPE.order,
      },
    ],
  },
  {
    titleKey: 'manageFields.sections.destination',
    options: [
      {
        key: MANAGE_FIELD_KEY.destinationBusinessName,
        labelKey: 'manageFields.options.businessName',
        scope: MANAGE_FIELD_SCOPE.order,
      },
      {
        key: MANAGE_FIELD_KEY.destinationNote,
        labelKey: 'manageFields.options.additionalNote',
        scope: MANAGE_FIELD_SCOPE.order,
      },
    ],
  },
  {
    titleKey: 'manageFields.sections.shipment',
    options: [
      {
        key: MANAGE_FIELD_KEY.shipmentTags,
        labelKey: 'manageFields.options.tags',
        scope: MANAGE_FIELD_SCOPE.order,
      },
      {
        key: MANAGE_FIELD_KEY.shipmentCustomerNote,
        labelKey: 'manageFields.options.customerNote',
      },
    ],
  },
  {
    titleKey: 'manageFields.sections.payment',
    options: [
      {
        key: MANAGE_FIELD_KEY.paymentDefaultMethods,
        labelKey: 'manageFields.options.defaultMethods',
        scope: MANAGE_FIELD_SCOPE.order,
      },
    ],
  },
]

export const ALL_MANAGE_FIELD_KEYS: readonly ManageFieldKey[] =
  MANAGE_FIELD_SECTIONS.flatMap((section) =>
    section.options.map((option) => option.key),
  )

function inScope(
  optionScope: ManageFieldScope | undefined,
  scope: ManageFieldScope,
): boolean {
  if (scope === MANAGE_FIELD_SCOPE.both) return true
  return (
    !optionScope ||
    optionScope === MANAGE_FIELD_SCOPE.both ||
    optionScope === scope
  )
}

export function getManageFieldSections(
  scope: ManageFieldScope,
): readonly ManageFieldSection[] {
  return MANAGE_FIELD_SECTIONS.map((section) => ({
    ...section,
    options: section.options.filter((option) => inScope(option.scope, scope)),
  })).filter((section) => section.options.length > 0)
}
